/* Capacity calculation model for the Cam calculator.
   Estimates how many tickets and hours of work a team can get through per
   month when handing a chosen share of its backlog to Cam, accounting for
   review overhead that scales with the volume under review. */

/* Review time per Cam pull request (in minutes) at the low and high ends of
   the share slider. Average review time rises as the review queue grows. */
export const REVIEW_START_MIN = 30;
export const REVIEW_END_MIN = 60;

/* Standard working day and month lengths used to convert saved hours into
   working days and full-time developer equivalents. */
export const WORKDAY_HOURS = 8;
export const WORKDAYS_A_MONTH = 20;

/* Default assumptions shown before the user customises the inputs. */
export const DEFAULT_HOURS_PER_TICKET = 4;
export const DEFAULT_SHARE = 0.4;
export const DEFAULT_BAND_ID = 'm';

/* Multiplier applied to today's monthly hours when choosing the top of the
   semicircular gauge, placing today's mark just left of centre. */
export const DIAL_HEADROOM = 2.5;

/* Day thresholds where the headline outcome copy switches from hours to
   working days and from working days to full-time developers. */
const DAYS_THRESHOLD = 11.5;
const DEVELOPERS_THRESHOLD = 19.5;

/* Typical monthly ticket volume by Gearset team size band. */
export const TEAM_SIZE_BANDS = [
	{ id: 's', label: '1 to 5', min: 1, avg: 17 },
	{ id: 'm', label: '6 to 10', min: 6, avg: 36 },
	{ id: 'l', label: '11 to 20', min: 11, avg: 63 },
	{ id: 'x', label: '21 or more', min: 21, avg: 100 },
];

const NUMBER_WORDS = [
	'no',
	'one',
	'two',
	'three',
	'four',
	'five',
	'six',
	'seven',
	'eight',
	'nine',
	'ten',
];

/**
 * Format a numeric value as a rounded UK-locale integer string.
 *
 * @param {number} value - Numeric value to format.
 * @returns {string} Rounded, comma-grouped integer string.
 */
export function formatNumber(value) {
	return Math.round(value).toLocaleString('en-GB');
}

/**
 * Convert small integers (0–10) to their English word form, leaving larger
 * numbers as digits.
 *
 * @param {number} value - Integer to format.
 * @returns {string} Word or digit representation.
 */
export function numberWord(value) {
	return value >= 0 && value <= 10 && Number.isInteger(value)
		? NUMBER_WORDS[value]
		: String(value);
}

/**
 * Look up a team size band definition by its identifier, falling back to the
 * default band when an unknown identifier is supplied.
 *
 * @param {string} bandId - Band identifier ('s', 'm', 'l', or 'x').
 * @returns {{ id: string, label: string, min: number, avg: number }} Matching band object.
 */
export function getBandById(bandId) {
	return TEAM_SIZE_BANDS.find(band => band.id === bandId)
		|| TEAM_SIZE_BANDS.find(band => band.id === DEFAULT_BAND_ID);
}

/**
 * Select the team size band that contains a given head count, falling back to
 * the default band when the count is not a positive number.
 *
 * @param {number} count - Number of team members using Gearset.
 * @returns {{ id: string, label: string, min: number, avg: number }} Matching band object.
 */
export function getBandForTeamSize(count) {
	if (!Number.isFinite(count) || count < 1) {
		return getBandById(DEFAULT_BAND_ID);
	}

	let matched = TEAM_SIZE_BANDS[0];
	for (const band of TEAM_SIZE_BANDS) {
		if (count >= band.min) {
			matched = band;
		}
	}

	return matched;
}

/**
 * Compute the average hours spent reviewing each Cam pull request at a given
 * backlog share.
 *
 * @param {number} share - Fraction of tickets handed to Cam (0 to 1).
 * @returns {number} Hours of review per Cam ticket.
 */
export function reviewHoursAt(share) {
	return (REVIEW_START_MIN + ((REVIEW_END_MIN - REVIEW_START_MIN) * share)) / 60;
}

/**
 * Round a positive number up to a clean 1 / 2 / 2.5 / 5 / 10 × 10^n ceiling
 * suitable for the maximum label on a gauge.
 *
 * @param {number} value - Raw upper bound.
 * @returns {number} Rounded gauge ceiling (minimum 10).
 */
export function niceMax(value) {
	if (value <= 0) {
		return 10;
	}

	const power = 10 ** Math.floor(Math.log10(value));
	const steps = [1, 2, 2.5, 5, 10];
	for (const step of steps) {
		if (step * power >= value) {
			return step * power;
		}
	}

	return 10 * power;
}

/**
 * Calculate monthly ticket throughput, net capacity added, and gauge positions
 * for a team handing a given share of tickets to Cam.
 *
 * @param {object} inputs - Calculator inputs.
 * @param {number} inputs.volume - Tickets completed per month today.
 * @param {number} inputs.hours - Hours a ticket takes end-to-end today.
 * @param {number} inputs.share - Fraction of tickets handed to Cam (0 to 1).
 * @returns {{
 *   before: number,
 *   after: number,
 *   more: number,
 *   camTickets: number,
 *   capacityHours: number,
 *   days: number,
 *   fte: number,
 *   beforeHours: number,
 *   afterHours: number,
 *   dialMax: number,
 *   beforeFraction: number,
 *   afterFraction: number,
 *   share: number,
 * }} Derived throughput, capacity, and gauge metrics.
 */
export function computeCapacity({ volume, hours, share }) {
	const before = Number.isFinite(volume) && volume >= 0 ? Math.round(volume) : 0;
	const cleanHours = Number.isFinite(hours) && hours >= 0 ? hours : 0;
	const cleanShare = Number.isFinite(share) ? Math.min(1, Math.max(0, share)) : 0;

	const teamHours = before * cleanHours;
	const reviewHours = reviewHoursAt(cleanShare);
	const blendedHoursPerTicket = ((1 - cleanShare) * cleanHours) + (cleanShare * reviewHours);

	const afterExact = cleanHours <= 0 || blendedHoursPerTicket <= 0
		? before
		: Math.max(before, teamHours / blendedHoursPerTicket);
	const after = Math.round(afterExact);
	const more = after - before;

	const camTickets = afterExact * cleanShare;
	const capacityHours = camTickets * Math.max(0, cleanHours - reviewHours);
	const days = capacityHours / WORKDAY_HOURS;
	const fte = days / WORKDAYS_A_MONTH;

	const beforeHours = Math.round(teamHours);
	const afterHours = beforeHours + Math.round(capacityHours);
	const dialMax = niceMax(teamHours * DIAL_HEADROOM);
	const beforeFraction = Math.min(100, (beforeHours / dialMax) * 100);
	const afterFraction = Math.min(100, (afterHours / dialMax) * 100);

	return {
		before,
		after,
		more,
		camTickets,
		capacityHours,
		days,
		fte,
		beforeHours,
		afterHours,
		dialMax,
		beforeFraction,
		afterFraction,
		share: cleanShare,
	};
}

/**
 * Choose the headline outcome unit and highlighted phrase for a capacity
 * calculation result.
 *
 * @param {{ share: number, more: number, capacityHours: number, days: number, fte: number }} result - Output from computeCapacity.
 * @returns {{ kind: 'none' | 'hours' | 'days' | 'developers', label: string }} Outcome classification and highlighted label.
 */
export function describeOutcome({ share, more, capacityHours, days, fte }) {
	if (share === 0 || more <= 0) {
		return { kind: 'none', label: '' };
	}

	if (days < DAYS_THRESHOLD) {
		return {
			kind: 'hours',
			label: `${formatNumber(capacityHours)} hours`,
		};
	}

	if (days < DEVELOPERS_THRESHOLD) {
		return {
			kind: 'days',
			label: `${Math.round(days)} working days`,
		};
	}

	const roundedHalfFte = Math.round(fte * 2) / 2;
	const whole = Math.floor(roundedHalfFte);
	const hasHalf = roundedHalfFte - whole > 0;

	let label;
	if (roundedHalfFte === 1) {
		label = 'a full-time developer';
	} else if (hasHalf) {
		const prefix = whole === 1 ? 'one and a half' : `${numberWord(whole)} and a half`;
		label = `${prefix} developers`;
	} else {
		label = `${numberWord(whole)} extra developers`;
	}

	return {
		kind: 'developers',
		label,
	};
}
